import { LitElement, type PropertyValues, html } from "lit";

import { customElement, property, query } from "lit/decorators.js";

import { styles } from "./wow-confirmation-drawer.css";

/**
 * A mobile-optimized confirmation drawer that slides up from the bottom of the screen.
 *
 * Features smooth slide-up/slide-down animations, backdrop overlay with blur effect,
 * and flexible content via slots (consistent with wow-modal pattern).
 *
 * @example
 * ```html
 * <wow-confirmation-drawer>
 *   <span slot="header">Are you sure you want to stop?</span>
 *   <div>
 *     <p>You have unsaved changes.</p>
 *     <wow-button variant="neutral" size="medium" @click=${this.handleSave}>
 *       Save and Continue
 *     </wow-button>
 *     <wow-button variant="neutral" size="medium" @click=${this.handleContinue}>
 *       Continue Editing
 *     </wow-button>
 *   </div>
 * </wow-confirmation-drawer>
 * ```
 *
 * @example
 * ```typescript
 * // Handle drawer events
 * drawer.addEventListener('close', () => {
 *   drawer.remove();
 * });
 * ```
 *
 * @fires close - Emitted when the drawer should be closed (backdrop click or programmatic close)
 *
 * @slot header - Content for the drawer title/header
 * @slot default - Main content area of the drawer
 *
 * @csspart drawer-overlay - The backdrop/overlay element
 * @csspart drawer-content - The main drawer container
 * @csspart drawer-header - The header section
 * @csspart drawer-body - The content area
 */
@customElement("wow-confirmation-drawer")
export class WowConfirmationDrawer extends LitElement {
  static override styles = styles;

  /**
   * When true, prevents the drawer from being closed through backdrop clicks.
   * The drawer can still be closed programmatically or via button actions.
   */
  @property({ type: Boolean, attribute: "prevent-close" })
  preventClose = false;

  @query(".drawer-overlay")
  private popoverElement!: HTMLElement;

  private animationTimeout: number | null = null;

  private handleToggle = async (e: Event) => {
    const toggleEvent = e as ToggleEvent;
    if (toggleEvent.newState === "closed" && !this.preventClose) {
      // User closed drawer via ESC
      await this.performCloseAnimation();
    }
  };

  private handleContentClick = (e: Event) => {
    // Stop clicks on drawer content from bubbling up to the overlay
    e.stopPropagation();
  };

  private handleCustomClose = async (e: Event) => {
    // Handle custom close events from slotted content
    e.stopPropagation();
    await this.performCloseAnimation();
  };

  /**
   * Sets the drawer's closing animation state by manipulating the `data-closing` attribute.
   *
   * This explicit DOM manipulation approach provides clear control over exit animations:
   * - Slide down (translateY(100%)) with backdrop opacity fade
   *
   * @param isClosing - Whether the drawer is in closing state
   */
  private setClosingState(isClosing: boolean) {
    if (isClosing) {
      this.setAttribute("data-closing", "");
    } else {
      this.removeAttribute("data-closing");
    }
  }

  /**
   * Performs the close animation sequence before dispatching the close event.
   *
   * This method:
   * 1. Sets the closing state to trigger CSS animations
   * 2. Waits for the animation duration (300ms)
   * 3. Resets the closing state
   * 4. Dispatches the 'close' event for parent components to handle
   *
   * @returns Promise that resolves when animation completes
   */
  private async performCloseAnimation(): Promise<void> {
    // Trigger the closing animation
    this.setClosingState(true);

    // Wait for the exit animation to complete
    return new Promise<void>((resolve) => {
      // Read animation duration from CSS custom property
      const durationInSeconds =
        parseFloat(getComputedStyle(this).getPropertyValue("--drawer-animation-duration-s")) || 0.3; // fallback to 0.3 seconds
      const animationDuration = durationInSeconds * 1000; // Convert to milliseconds

      this.animationTimeout = window.setTimeout(() => {
        this.setClosingState(false);
        this.dispatchCloseEvent();
        resolve();
      }, animationDuration);
    });
  }

  /**
   * Dispatches a 'close' event that parent components should listen for.
   *
   * The parent is responsible for actually removing or hiding the drawer element.
   * This separation allows for flexible drawer lifecycle management.
   *
   * @example
   * ```typescript
   * drawer.addEventListener('close', () => {
   *   // Remove from DOM
   *   drawer.remove();
   *
   *   // Or hide with CSS
   *   drawer.style.display = 'none';
   *
   *   // Or remove from framework state
   *   this.showDrawer = false;
   * });
   * ```
   *
   * @fires close - Custom event with no additional data
   */
  private dispatchCloseEvent() {
    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  override connectedCallback() {
    super.connectedCallback();
    // Listen for custom close events from slotted content
    this.addEventListener("wow-drawer-close", this.handleCustomClose);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up animation timeout
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
    // Clean up event listener
    this.removeEventListener("wow-drawer-close", this.handleCustomClose);
    // Restore body scroll
    document.body.style.overflow = "unset";
  }

  override firstUpdated(props: PropertyValues) {
    super.firstUpdated(props);
    if (this.popoverElement) {
      this.popoverElement.addEventListener("toggle", this.handleToggle);
      // Show the drawer when component is first rendered
      this.popoverElement.showPopover();
      // Prevent body scroll when drawer is open
      document.body.style.overflow = "hidden";
    }
  }

  protected override render() {
    return html`
      <div class="drawer-overlay" popover="auto" part="drawer-overlay">
        <div class="drawer-content" part="drawer-content" @click=${this.handleContentClick}>
          <div class="drawer-header" part="drawer-header">
            <slot name="header"></slot>
          </div>

          <div class="drawer-body" part="drawer-body">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "wow-confirmation-drawer": WowConfirmationDrawer;
  }
}
